/**
 * The single source of navigation truth.
 *
 * SECTIONS ARE COSMETIC. session.user decides what is DRAWN; the server
 * decides what is reachable, and every gated route answers 404 to a caller
 * who should not have it whether or not the link was rendered.
 *
 * `react: false` means the page is still Jinja, served through this origin by
 * the frontend's proxy. Each becomes `true` as its pass ports it — this list
 * is the migration's own progress bar.
 */
import type { Session } from '../../lib/api'

export interface NavItem {
  label: string
  icon: string
  href: string
  react: boolean
  activePrefixes: string[]
  /** Sub-group heading within a section. Rendered once, above the first item
   *  carrying it; consecutive items sharing a group sit under one heading.
   *  Groups the club rail by the job (Review / Analysis / Setup) without
   *  splitting the section or moving any route (Ben, 2026-10-23). */
  group?: string
  struck?: boolean
  /** Nothing serves this path yet. Rendered, disabled and labelled — a link
   *  that silently redirects somewhere else is worse than one that says it is
   *  not built. */
  pending?: boolean
  /** Match the paths exactly, never as a prefix. Needed when one rail item's
   *  href is a prefix of another's: /pipeline is Pipeline Operations and
   *  /pipeline/qa-studio is QA Studio, and without this BOTH light up while
   *  you are on QA Studio. */
  exact?: boolean
  /** Open in a new tab: an instrument run beside the product (the UAT
   *  runner), not a page of it. Only for non-react items. */
  newTab?: boolean
}

export interface NavSection {
  title: string
  items: NavItem[]
  /** The club's own section: the OrgSwitcher renders above it, and its
   *  styling differs. A flag, not a title match — the title is free text. */
  club?: boolean
}

/* PRIORI PLATFORM — running the platform itself, not any club's data.
   Platform admins only (Ben, 2026-08-28): these are the screens that change
   who can do what, and read access to a club's video does not imply it. */
/* The platform's operational screens: staff (platform.read) see them too,
   so they are named here and spliced into both rails rather than living
   inside the admin-only array. One section, Priori Platform (Ben,
   2026-10-16): Video & Data Pipeline is gone.
   - Match Administration: Layers Management renamed, with Manage Games and
     the old QA Studio folded in as its Games and Legacy QA tabs.
   - Pipeline Operations: the jobs board. /pipeline, exact: only that page.
   - Labeling: the labeling suite; NOT exact, every sub-labeler lights it. */
const STAFF_PLATFORM_ITEMS: NavItem[] = [
  { label: 'Match Administration', icon: 'layers', href: '/ops/layer-stack',
    react: true, activePrefixes: ['/ops/layer-stack', '/ops/match-lab',
                                    '/ops/video-times', '/manage-games',
                                    '/pipeline/qa-studio', '/qa'] },
  { label: 'Tracking QA', icon: 'film', href: '/ops/tracking-qa',
    react: true, activePrefixes: ['/ops/tracking-qa', '/ops/video-qa'] },
  { label: 'Pipeline Operations', icon: 'wrench', href: '/pipeline',
    react: true, activePrefixes: ['/pipeline'], exact: true },
  { label: 'Labeling', icon: 'focus', href: '/labeling',
    react: true, activePrefixes: ['/labeling'] },
]

const PLATFORM_ITEMS: NavItem[] = [
  // First, because it is where staff land and it is now the whole picture:
  // Resiliency & API merged into it (Ben, 2026-08-28). Two screens describing
  // one build meant two places to look and two chances to be stale.
  // Database Info folded in as a tab (Ben, 2026-08-30), joining Resiliency &
  // API: one screen for "what shape is this environment in" rather than three
  // rail entries asking the same question.
  { label: 'Status', icon: 'activity', href: '/status',
    react: true,
    activePrefixes: ['/status', '/resiliency', '/database-info',
                     // The legacy Flask URL, matched so the rail highlights
                     // when a browser is on it. api-surface: not-a-call
                     '/admin/database-info'] },
  // Split by WHO, not by table (Ben, 2026-08-29). Our people and their
  // platform access is one job; a club, what we know about it and who at it
  // has access is another. The single all-accounts list they replaced put the
  // same person in two places and could not answer "who can reach this club".
  { label: 'Manage Users', icon: 'users', href: '/manage-users',
    react: true, activePrefixes: ['/manage-users', '/manage-staff', '/users'] },
  { label: 'Manage Clubs', icon: 'building', href: '/manage-clubs',
    react: true, activePrefixes: ['/manage-clubs', '/manage-orgs'] },
  ...STAFF_PLATFORM_ITEMS,
  // Per-prospect upload share links. React page at /lead-links; the legacy
  // Flask page stays at /admin/lead-links (proxied) and the rail highlights
  // on either. api-surface: not-a-call
  { label: 'Lead Links', icon: 'link', href: '/lead-links',
    react: true, activePrefixes: ['/lead-links', '/admin/lead-links'] },
  { label: 'Activity', icon: 'activity', href: '/activity',
    react: true, activePrefixes: ['/activity'] },
  // ONE entry, three tabs (Ben, 2026-08-30). The route inventory and the
  // cross-club token screen answered the same question — what does this API
  // serve and who can call it — from two rail entries. The club-facing screen
  // took the "Developer Tools" name.
  { label: 'Manage API', icon: 'code', href: '/manage-api',
    react: true,
    activePrefixes: ['/manage-api', '/manage-api-tokens', '/settings'] },
]

/* TACTICAL INSIGHTS LAB — manage insights and collaborate (Ben, 2026-09-02).
   Two items: Manage Insights (the analysis tool, formerly Insights Lab) and
   the Insights Lab (the scratchpads and the builder behind one rail entry). */
const TACTICAL_INSIGHTS_ITEMS: NavItem[] = [
  // At /manage-insights since 2026-09-07, so the address says what the rail
  // says; it held /insights-lab until the lab below took that URL.
  // /insights-packs stays an active prefix because pack REPORT deep links
  // still arrive there and redirect; the rail should light up on the way.
  { label: 'Manage Insights', icon: 'pulse', href: '/manage-insights',
    react: true, activePrefixes: ['/manage-insights', '/insights-packs'] },
  // Six-tab page (AnalyticsCollaboration.tsx): Analytics Collaboration (NL
  // canvas), Build a dashboard (widgets on one synced playhead), Segment
  // Workbench, Insights Playground (ad-hoc SQL + visuals), Pitch Playground
  // (vertical pitch layers), and Visual Builder (analysis authoring over the
  // semantic catalog — folded in from its own rail entry, Ben 2026-09-02).
  // At /insights-lab since 2026-09-07 to match this label; the old addresses
  // stay as active prefixes because they redirect into the tabs.
  { label: 'Insights Lab', icon: 'flask', href: '/insights-lab',
    react: true,
    activePrefixes: ['/insights-lab', '/analytics-collaboration',
                     '/insights-playground', '/insights-pitch',
                     '/visual-builder'] },
]

/** The club's own work. Titled with the club's name, so a staff member
 *  reading two rails apart cannot mistake one club for another. */
function tacticalLab(session: Session): NavItem[] {
  // Grouped by the job, not by feature age (Ben, 2026-10-23): Dashboard is
  // the landing and stands alone; Review holds the daily viewing surfaces;
  // Analysis holds the feed and the workshop; Setup holds the occasional
  // acts. The groups are headings inside one section — no route moved.
  const items: NavItem[] = [
    { label: 'Dashboard', icon: 'gauge', href: '/dashboard',
      react: true, activePrefixes: ['/dashboard', '/home'] },
    { label: 'Match Room', icon: 'grid', href: '/match-room',
      react: true, group: 'Review',
      // Every name the match surface has had (Match Room since 2026-10-23).
      // The rail highlights by prefix, so a missed one leaves nothing looking
      // active while you are on the page it belongs to.
      activePrefixes: ['/match-room', '/matches', '/games', '/match-video',
                       '/tactical-lab', '/game-match-summary'] },
    // The Clip Room (Ben, 2026-10-23): reels and the match list in one
    // home — the Reels rail entry folded in the same day it appeared. The
    // Clip Lab at /clip-room/<code> is the tool inside it, and the reel
    // deep links (/playlists/<id> editor and player) light this entry while
    // they keep their addresses.
    { label: 'Clip Room', icon: 'scissors', href: '/clip-room',
      react: true, group: 'Review',
      activePrefixes: ['/clip-room', '/clip-lab', '/playlists', '/reels'] },
  ]
  if (session.home_is_feed) {
    // 'Insights Feed', not 'Insights': a staff screen already reads Manage
    // Insights and Insights Lab, and the club rail once also showed Insights
    // Workshop — four near-identical labels. The feed is named for what it
    // is (Ben, 2026-10-23).
    items.push({ label: 'Insights Feed', icon: 'pulse', href: '/insights',
      // Both prefixes stay: /feed still resolves, as a redirect, and the rail
      // should highlight while the browser is briefly on it.
      react: true, group: 'Analysis', activePrefixes: ['/insights', '/feed'] })
  }
  // Under the feed (Ben, 2026-10-08). The server decides: staff always
  // (unless viewing as someone), else the club's workshop flag.
  if (session.workshop) {
    // Analysis Room (Ben, 2026-10-21; the Insights Workshop before). The old
    // address redirects; it stays a prefix so the rail lights mid-redirect.
    items.push({ label: 'Analysis Room', icon: 'flask', href: '/analysis-room',
      react: true, group: 'Analysis',
      activePrefixes: ['/analysis-room', '/insights-workshop'] })
  }
  items.push(
    // Below the viewing surfaces, above Developer Tools (Ben, 2026-10-15):
    // uploading is an occasional act, not the daily entry point.
    { label: 'Upload Match', icon: 'upload', href: '/upload-match',
      react: true, group: 'Setup', activePrefixes: ['/upload-match'] },
    // Glossary is NOT here: it is the book icon in the sidebar footer
    // (Sidebar.tsx), alongside notifications and sign-out. It was in both,
    // and a rail that lists the same destination twice makes the reader
    // wonder what the difference is (Ben, 2026-08-29).
    // The club's own keys and the API reference, one screen (Ben,
    // 2026-08-30). Named Developer Tools because that is what a customer's
    // engineer looks for; the staff screen that held the name is now
    // Manage API.
    { label: 'Developer Tools', icon: 'code', href: '/developer-tools',
      react: true, group: 'Setup',
      activePrefixes: ['/developer-tools', '/api-tokens'] },
  )
  // Last, and only for the people a platform admin enabled for UAT in this
  // club (Manage Clubs; the server decides `uat`, and the runner's routes
  // ask again). Its own tab: a session measures the product beside it.
  if (session.uat) {
    items.push({ label: 'Run UAT', icon: 'flask', href: '/api/v1/uat/runner',
      react: false, newTab: true, group: 'Setup', activePrefixes: [] })
  }
  return items
}

export function buildNav(session: Session | null): NavSection[] {
  if (!session) return []
  const sections: NavSection[] = []

  if (session.user.is_platform_admin) {
    sections.push({ title: 'Priori Platform', items: PLATFORM_ITEMS })
  } else if (session.user.is_staff) {
    // Staff get the one platform screen they are authorized for, under the
    // same heading. `else if`, not a second push: two sections with the same
    // title would be a rail bug, and an admin is also staff.
    sections.push({ title: 'Priori Platform', items: STAFF_PLATFORM_ITEMS })
  }
  if (session.user.is_staff) {
    sections.push({ title: 'Tactical Insights Lab', items: TACTICAL_INSIGHTS_ITEMS })
  }
  // The club section appears only once a club is in context. Staff arrive
  // with none selected, and a rail of club links pointing at nothing would
  // invite exactly the mistake of acting on the wrong club.
  if (session.org) {
    sections.push({
      // One product name for every club (Ben, 2026-10-23; it carried the
      // club's name before). Which club is in context is the OrgSwitcher's
      // job, which sits directly above this section.
      title: 'Tactical Football Platform',
      club: true,
      items: tacticalLab(session),
    })
  }
  return sections
}

/** Every rail destination, flattened, for the Status page.
 *
 *  The nav is part of what this build IS: which paths exist, which are React
 *  and which are still proxied to Flask, and which are not built. Rendering it
 *  from the same function the rail uses means the documentation cannot drift
 *  from the thing it documents — and the react flags double as the migration's
 *  progress board.
 */
export function navInventory(session: Session): {
  section: string
  label: string
  href: string
  served: 'react' | 'flask' | 'not built'
}[] {
  return buildNav(session).flatMap((s) =>
    s.items.map((i) => ({
      section: s.title,
      label: i.label,
      href: i.href,
      served: i.pending ? ('not built' as const)
            : i.react ? ('react' as const)
            : ('flask' as const),
    })),
  )
}

export function isNavActive(item: NavItem, pathname: string): boolean {
  // Segment-aware, not a bare startsWith: '/insights' must not light up for
  // '/insights-lab'. And exact when the item says so, because a rail item
  // whose href is a prefix of another's would otherwise light up for both.
  if (item.exact) return item.activePrefixes.includes(pathname)
  return item.activePrefixes.some((p) => pathname === p || pathname.startsWith(p + '/'))
}

/** Where signing in lands you.
 *
 *  Staff and platform admins land on Status: they arrive to answer "is the
 *  platform healthy", not "how did my team play". Everyone else lands in
 *  their club, because that is the only thing they have.
 */
export function landingPath(session: Session): string {
  if (session.user.is_platform_admin || session.user.is_staff) return '/status'
  return '/dashboard'
}


/**
 * Where to land after switching club.
 *
 * A switch used to be `window.location.reload()`, which keeps the URL. On a
 * game-scoped page that reloads a match the new club does not own — the API
 * correctly refuses it (performance_summary._require_game_in_org), so the
 * screen came back empty and the switch looked broken rather than the URL
 * looking wrong.
 *
 * Anything addressed by game code is club-specific by construction. Everything
 * else — the dashboard, settings, the admin screens — means the same thing in
 * any club, so it reloads in place.
 */
const GAME_SCOPED_PREFIXES = [
  '/match-room/',
  '/clip-room/',
  '/clip-lab/',
  // Old match-page addresses: a club switch can land mid-redirect, and the
  // answer should be the same on the way through as at the destination.
  '/match-video/',
  '/tactical-lab/',
  '/game-match-summary/',
  '/performance-summary/',
  '/attacking-match-summary/',
  '/defending-match-summary/',
  '/game/',
]

export function pathAfterOrgSwitch(pathname: string): string | null {
  const scoped = GAME_SCOPED_PREFIXES.some((p) => pathname.startsWith(p))
  // null means "stay here and reload" — said explicitly so the caller does not
  // have to compare the answer against its own current path.
  return scoped ? '/match-room' : null
}
